//hook
import { UserConsumer } from '../hooks/context/UserContext';
import { NotificationConsumer } from '../hooks/context/NotificationContext';

//type
import { completedHostReqType, hostProfileType } from '../api/completedRole/type/CompletedRoleType';

//api
import { PostCompletedRole } from '../api/completedRole/PostCompletedRole';
import { GetProfile } from '../api/profile/GetProfile';
import { UpdateCompletedRole } from '../api/completedRole/UpdateCompletedRole';

//formik and yap
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

//mui
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

//translate
import { useTranslation } from 'react-i18next';
import i18n from '../translate/i18n';

interface driverFieldProps {
    setOpenCompletedProfile: (open: boolean) => void
    loading: boolean
    setLoading: (loading: boolean) => void
    hostProfile: hostProfileType,
    setHostProfile:(clientProfile: hostProfileType) => void
    hostCommercialRegistry: string,
    setHostCommercialRegistry: (hostCommercialRegistry: string) => void
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const storageURL = process.env.REACT_APP_STORAGE_BASE_URL;

const CompletedHostField = (
    {
    setOpenCompletedProfile, 
    loading, 
    setLoading,
    hostProfile,
    setHostProfile,
    hostCommercialRegistry,
    setHostCommercialRegistry
    }: driverFieldProps
) => {
    const { t } = useTranslation();
    let {userData,setUserData, theme} = UserConsumer()
    let {setNoteMessage, setShowMessage, setStatusCode} = NotificationConsumer()
    
    //validation
    const hostSchema = Yup.object().shape({
        company_name: Yup.string().required(t("company_name_required")),
        commercial_registry: Yup.mixed()
        .when("is_company", (is_company, schema) => {
            const companyFlag = Number(is_company);
            if (companyFlag === 1) {
                return schema
                .required(t("commercial_registry_image_required"))
                .test("fileType", t("invalid_file_type"), (value) => {
                if (!value) return false;
                if (typeof value === "string") return true;
                return ["image/jpeg", "image/png", "image/jpg"].includes((value as File).type);
                })
            }
            else {
                return schema.nullable();
            }
        })
    })

    //convert url to file
    const convertUrlToFile = async (url: string) => {
        const response = await fetch(`${storageURL}/${url}`);
        const blob = await response.blob();
        const ext = url.split('.').pop() || 'png';
        const fileName = url.split('/').pop()?.split('.').slice(0, -1).join('') || 'file';
        return new File([blob], `${fileName}.${ext}`, { type: blob.type });
    };

    //submit
    const handleSubmit = async (values: completedHostReqType, theme:string)=> {
        if(values.is_company === 0) {
            delete values.commercial_registry
        }
        console.log(values)
        try {
            setLoading(true)
            let result:any = ''
            if(userData?.is_host) {
                if ('commercial_registry' in values && typeof values.commercial_registry === "string") {
                    values.commercial_registry = await convertUrlToFile(values.commercial_registry);
                }
                result = await UpdateCompletedRole(values, theme, hostProfile?.id);
            }
            else {
                result = await PostCompletedRole(values, theme);
            }
            console.log(`completed driver successful:`);
            if(result.code == 200) {
                setNoteMessage(result.message)
                setShowMessage(true)
                setStatusCode(result.code)
                setOpenCompletedProfile(false)
                const resultprofile = await GetProfile();
                setUserData(resultprofile.data);
                setHostProfile({   
                    id:0,
                    user_id:0,
                    is_company:0,
                    company_name:"",
                    commercial_registry:null,
                    is_confirmed:false,
                })
            }
            else {
                setNoteMessage(result.message)
                setShowMessage(true)
                setStatusCode(result.code)
            }
        }
        catch (error:any) {
            console.error('completed failed', error);
            setNoteMessage(error.response.data.message)
            setShowMessage(true)
            setStatusCode(error.status)
        }
        finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <Formik
            enableReinitialize
            initialValues={{
                is_company: hostProfile?.is_company ?? 0,
                company_name: hostProfile?.company_name ?? "",
                commercial_registry: hostProfile?.commercial_registry ?? null,
            }}
            validationSchema={hostSchema}
            onSubmit={(values)=>handleSubmit(values, theme)}
            >
            {({ values,touched, errors,setFieldValue }) => (
                <Form>
                    <div>
                        <FormGroup>
                            <FormControlLabel 
                            name='is_company' 
                            control={<Switch checked={values.is_company === 1} onChange={(e) => setFieldValue('is_company', e.target.checked ? 1 : 0)} />} label={t("is_company")} />
                        </FormGroup>
                        <Field
                            as={TextField}
                            name="company_name"
                            label={t("company_name")}
                            className="sm:w-1/2 w-full !mt-2"
                            helperText={touched.company_name && errors.company_name ? errors.company_name : ""}
                            error={touched.company_name && Boolean(errors.company_name)}
                        />
                    </div>
                    <div className={values.is_company === 1 ? "block mt-5" : "hidden"}>
                        <Button
                            component="label"
                            className='!bg-paretnerColor !text-white w-fit !capitalize'
                            startIcon={<CloudUploadIcon />}
                        >
                            {t("commercial_registry_image")}
                            <VisuallyHiddenInput
                            type="file"
                            accept="image/*"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                setFieldValue("commercial_registry", file);
                                setHostCommercialRegistry(URL.createObjectURL(file));
                                }
                            }}
                            />
                        </Button>
                        {/* Show selected image preview */}
                        {hostCommercialRegistry 
                            ? (
                                <img
                                src={hostCommercialRegistry}
                                alt="Back license preview"
                                className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                />
                            ) 
                            : userData?.is_host && hostProfile?.commercial_registry ? (
                                // Show existing uploaded image from backend
                                <img
                                src={`${storageURL}/${hostProfile.commercial_registry}`}
                                alt="Back license uploaded"
                                className="mt-3 w-40 h-28 object-cover rounded-lg border"
                                />
                            ) : null
                        }
                        <p className="text-[12px] mt-2 text-red-500">
                            {touched.commercial_registry && errors.commercial_registry ? errors.commercial_registry : ""}
                        </p>
                    </div>
                    <hr className='mt-5'/>
                    <DialogActions className={i18n.language === "en" ? "!justify-start" : "!justify-start"}>
                        <Button className='w-full !bg-paretnerColor !text-white !rounded-full' type="submit">
                            {loading 
                            ? <CircularProgress className='w-1 h-1'/>
                            :t("confirm")
                            }
                        </Button>
                    </DialogActions>
                </Form>
            )}
            </Formik>
        </div>
    )
}

export default CompletedHostField